import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle 
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, 
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, Download, FileText, Package, Clock, CheckCircle, 
  XCircle, AlertCircle, Search, Loader2, ChevronRight, 
  Plus, Edit, Trash2, Image as ImageIcon, Check, X,
  GitMerge, RefreshCw, ShieldAlert, Crown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  
  // P0: Pagination & Search State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const [stats, setStats] = useState({
    total_products: 0,
    with_image: 0,
    without_image: 0
  });

  // P0: CRUD State
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // P0: Multi-Select & Bulk Delete State
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  
  // Vorschläge-Tab: Pending Products
  const [pendingProducts, setPendingProducts] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  
  // Duplicate confirmation dialog state
  const [dupConfirmOpen, setDupConfirmOpen] = useState(false);
  const [dupConfirmData, setDupConfirmData] = useState(null); // { mappingId, productName, pendingProduct, possibleDuplicates }

  // ===== DUPLIKATE TAB STATE =====
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  // selectedKeepIds: { [groupIndex]: productId }
  const [selectedKeepIds, setSelectedKeepIds] = useState({});
  // finalNames: { [groupIndex]: string } – editierbarer Name für das zusammengeführte Produkt
  const [finalNames, setFinalNames] = useState({});
  
  // Form fields
  const [gtin, setGtin] = useState('');
  const [productName, setProductName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // P0: Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1); // Reset to page 1 on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pre-fetch pending products count on initial load to show badge
  useEffect(() => {
    fetchPendingProducts();
  }, []);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'history') {
      fetchImportHistory();
    } else if (activeTab === 'pending') {
      fetchPendingProducts();
    } else if (activeTab === 'duplicates') {
      fetchDuplicates();
    }
  }, [activeTab, page, pageSize, debouncedSearchQuery]);

  // P0: Clear selection when page changes
  useEffect(() => {
    setSelectedProductIds([]);
  }, [page, pageSize]);

  const fetchPendingProducts = async () => {
    try {
      setPendingLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/pending-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingProducts(res.data || []);
    } catch (err) {
      console.error('Failed to load pending products:', err);
    } finally {
      setPendingLoading(false);
    }
  };

  const approvePending = async (mappingId, productName, force = false) => {
    setProcessingId(mappingId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/superadmin/approve-product`,
        { mapping_id: mappingId, product_name: productName, force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // New 2-phase flow: requires_confirmation = show dialog BEFORE approving
      if (res.data?.requires_confirmation) {
        setDupConfirmData({
          mappingId,
          productName,
          pendingProduct: res.data.pending_product,
          possibleDuplicates: res.data.possible_duplicates || []
        });
        setDupConfirmOpen(true);
        return;
      }
      // Legacy fallback: old backend returned possible_duplicate AFTER approving
      if (res.data?.possible_duplicate) {
        toast.warning(`Freigegeben. Hinweis: Ähnliches Produkt existiert bereits: "${res.data.possible_duplicate.product_name}" (EAN: ${res.data.possible_duplicate.ean})`);
        fetchPendingProducts();
        return;
      }
      toast.success(`"${productName}" freigegeben. Kein Duplikat gefunden.`);
      fetchPendingProducts();
    } catch (err) {
      toast.error('Fehler bei der Freigabe');
    } finally {
      setProcessingId(null);
    }
  };

  const forceApprovePending = async () => {
    if (!dupConfirmData) return;
    const { mappingId, productName } = dupConfirmData;
    setDupConfirmOpen(false);
    await approvePending(mappingId, productName, true);
    setDupConfirmData(null);
  };

  const rejectPending = async (mappingId, productName) => {
    setProcessingId(mappingId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/superadmin/reject-product`,
        { mapping_id: mappingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${productName}" wurde abgelehnt.`);
      fetchPendingProducts();
    } catch (err) {
      toast.error('Fehler beim Ablehnen');
    } finally {
      setProcessingId(null);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (debouncedSearchQuery) {
        params.append('q', debouncedSearchQuery);
      }
      
      const response = await axios.get(`${API}/superadmin/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProducts(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalProducts(response.data.total || 0);
      
      // Calculate stats
      const total = response.data.total || 0;
      const withImage = (response.data.items || []).filter(p => p.image_url).length;
      setStats({
        total_products: total,
        with_image: withImage,
        without_image: total - withImage
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const fetchImportHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/products/import-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportHistory(response.data.imports || []);
    } catch (error) {
      console.error('Error fetching import history:', error);
      toast.error('Fehler beim Laden der Import-Historie');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/products/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'produkte_vorlage.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV-Vorlage heruntergeladen');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Fehler beim Herunterladen der Vorlage');
    }
  };

  // P0: Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild ist zu groß (max. 5 MB)');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // P0: Reset form
  const resetForm = () => {
    setGtin('');
    setProductName('');
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // P0: Open Add Dialog
  const handleOpenAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  // P0: Open Edit Dialog
  const handleOpenEditDialog = (product) => {
    setSelectedProduct(product);
    setGtin(product.gtin || '');
    setProductName(product.name || '');
    setImagePreview(product.image_url || '');
    setImageFile(null);
    setShowEditDialog(true);
  };

  // P0: Open Delete Dialog
  const handleOpenDeleteDialog = (product) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  };

  // P0: Create Product
  const handleCreateProduct = async () => {
    if (!gtin.trim()) {
      toast.error('Bitte geben Sie eine GTIN ein');
      return;
    }
    if (!productName.trim()) {
      toast.error('Bitte geben Sie einen Produktnamen ein');
      return;
    }

    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        gtin: gtin.trim(),
        name: productName.trim(),
        image_data: imagePreview || null
      };

      await axios.post(`${API}/admin/master-products`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Produkt erfolgreich erstellt');
      setShowAddDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error creating product:', error);
      const message = error.response?.data?.detail || 'Fehler beim Erstellen des Produkts';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  // P0: Update Product
  const handleUpdateProduct = async () => {
    if (!gtin.trim()) {
      toast.error('Bitte geben Sie eine GTIN ein');
      return;
    }
    if (!productName.trim()) {
      toast.error('Bitte geben Sie einen Produktnamen ein');
      return;
    }

    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        gtin: gtin.trim(),
        name: productName.trim(),
        image_data: imagePreview || ''
      };

      await axios.put(`${API}/admin/master-products/${selectedProduct.id}`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Produkt erfolgreich aktualisiert');
      setShowEditDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      const message = error.response?.data?.detail || 'Fehler beim Aktualisieren des Produkts';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  // P0: Delete Product
  const handleDeleteProduct = async () => {
    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');

      await axios.delete(`${API}/admin/master-products/${selectedProduct.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Produkt erfolgreich gelöscht');
      setShowDeleteDialog(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      const message = error.response?.data?.detail || 'Fehler beim Löschen des Produkts';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  // P0: Multi-Select Handlers
  const handleSelectProduct = (productId) => {
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProductIds.length === products.length) {
      // Deselect all
      setSelectedProductIds([]);
    } else {
      // Select all products on current page
      setSelectedProductIds(products.map(p => p.id));
    }
  };

  const handleOpenBulkDeleteDialog = () => {
    if (selectedProductIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Produkt aus');
      return;
    }
    setShowBulkDeleteDialog(true);
  };

  const handleBulkDelete = async () => {
    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API}/admin/master-products/bulk-delete`,
        { product_ids: selectedProductIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || `${selectedProductIds.length} Produkte gelöscht`);
      setShowBulkDeleteDialog(false);
      setSelectedProductIds([]);
      fetchProducts();
    } catch (error) {
      console.error('Error bulk deleting products:', error);
      const message = error.response?.data?.detail || 'Fehler beim Löschen der Produkte';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const fetchDuplicates = async () => {
    try {
      setDuplicatesLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/superadmin/products/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicates(res.data.groups || []);
      setSelectedKeepIds({});
      setFinalNames({});
    } catch (err) {
      console.error('Fehler beim Laden der Duplikate:', err);
      toast.error('Fehler beim Laden der Duplikate');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  // Wenn ein Produkt als Master ausgewählt wird, befülle den Namens-Input mit dem Produktnamen
  const handleSelectKeep = (groupIndex, productId, productName) => {
    setSelectedKeepIds(prev => ({ ...prev, [groupIndex]: productId }));
    // Nur vorbelegen wenn noch kein individueller Name eingetragen wurde
    setFinalNames(prev => ({
      ...prev,
      [groupIndex]: prev[groupIndex] !== undefined ? prev[groupIndex] : (productName || '')
    }));
  };

  const mergeDuplicates = async (groupIndex) => {
    const group = duplicates[groupIndex];
    const keepId = selectedKeepIds[groupIndex];
    if (!keepId) {
      toast.error('Bitte wähle zuerst das Hauptprodukt aus');
      return;
    }
    const finalName = (finalNames[groupIndex] || '').trim();
    if (!finalName) {
      toast.error('Bitte gib einen finalen Produktnamen ein');
      return;
    }
    const deleteIds = group.products.filter(p => p.id !== keepId).map(p => p.id);
    if (deleteIds.length === 0) {
      toast.error('Keine Duplikate zum Löschen');
      return;
    }
    try {
      setMergeLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/superadmin/products/merge`,
        { keep_id: keepId, delete_ids: deleteIds, final_name: finalName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Duplikate zusammengeführt');
      fetchDuplicates();
    } catch (err) {
      console.error('Merge-Fehler:', err);
      toast.error(err.response?.data?.detail || 'Fehler beim Zusammenführen');
    } finally {
      setMergeLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Wartend', variant: 'secondary', icon: Clock },
      running: { label: 'Läuft', variant: 'default', icon: Loader2 },
      completed: { label: 'Abgeschlossen', variant: 'default', icon: CheckCircle },
      failed: { label: 'Fehlgeschlagen', variant: 'destructive', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  // P0: Pagination Handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to page 1
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show ellipsis logic
      if (page <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Produktverwaltung</h1>
          <p className="text-muted-foreground mt-2">
            Master-Produktdatenbank verwalten und CSV-Import durchführen
          </p>
        </div>

        {/* Stats Cards */}
        {activeTab === 'products' && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gesamt Produkte</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_products}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Mit Bild</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.with_image}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ohne Bild</CardTitle>
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.without_image}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">CSV Upload</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Produkte</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Historie</span>
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2 relative" data-testid="tab-pending-products">
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Vorschläge</span>
              {pendingProducts.length > 0 && (
                <span className="ml-1 bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
                  {pendingProducts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="duplicates" className="flex items-center gap-2 relative" data-testid="tab-duplicates">
              <GitMerge className="h-4 w-4" />
              <span className="hidden sm:inline">Duplikate</span>
              {duplicates.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
                  {duplicates.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* CSV Upload Tab */}
          <TabsContent value="upload" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>CSV-Import</CardTitle>
                <CardDescription>
                  Laden Sie eine CSV-Datei hoch, um Produkte zur Master-Datenbank hinzuzufügen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Download className="h-4 w-4 mr-2" />
                  CSV-Vorlage herunterladen
                </Button>
                
                <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Datei hierher ziehen oder klicken</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    CSV oder Excel (.xlsx) • Max. 5MB • Max. 5000 Zeilen
                  </p>
                  <Button 
                    onClick={() => navigate('/superadmin/products/import')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    CSV/Excel hochladen
                  </Button>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Format-Anforderungen
                  </h4>
                  <ul className="text-sm space-y-1 ml-6 list-disc">
                    <li>Formate: CSV oder Excel (.xlsx)</li>
                    <li>Spalten: gtin, name (Header erforderlich)</li>
                    <li>GTIN: Numerisch (Pflicht)</li>
                    <li>Name: Produktname (Pflicht)</li>
                    <li>Encoding (CSV): UTF-8 mit/ohne BOM</li>
                    <li>Excel: Erstes Tabellenblatt</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            {/* P0: Bulk Action Bar */}
            {selectedProductIds.length > 0 && (
              <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                <CardContent className="py-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                      {selectedProductIds.length} {selectedProductIds.length === 1 ? 'Produkt' : 'Produkte'} ausgewählt
                    </span>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedProductIds([])}
                        className="flex-1 sm:flex-none"
                      >
                        Auswahl aufheben
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleOpenBulkDeleteDialog}
                        className="flex-1 sm:flex-none"
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Löschen ({selectedProductIds.length})
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle>Alle Produkte ({totalProducts})</CardTitle>
                    <CardDescription>Master-Produktdatenbank • Seite {page} von {totalPages}</CardDescription>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-72">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="GTIN oder Name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Button onClick={handleOpenAddDialog} className="w-full sm:w-auto">
                      <Plus className="h-4 w-4 mr-2" />
                      Produkt hinzufügen
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {searchQuery ? 'Keine Produkte gefunden' : 'Noch keine Produkte vorhanden'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Desktop: Tabelle */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr>
                            <th className="text-left p-2 font-medium text-sm w-12">
                              <Checkbox
                                checked={selectedProductIds.length === products.length && products.length > 0}
                                onCheckedChange={handleSelectAll}
                              />
                            </th>
                            <th className="text-left p-2 font-medium text-sm">Bild</th>
                            <th className="text-left p-2 font-medium text-sm">GTIN</th>
                            <th className="text-left p-2 font-medium text-sm">Produktname</th>
                            <th className="text-right p-2 font-medium text-sm">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product) => (
                            <tr key={product.id} className="border-b hover:bg-muted/50">
                              <td className="p-2">
                                <Checkbox
                                  checked={selectedProductIds.includes(product.id)}
                                  onCheckedChange={() => handleSelectProduct(product.id)}
                                />
                              </td>
                              <td className="p-2">
                                {product.image_url ? (
                                  <img 
                                    src={product.image_url} 
                                    alt={product.name}
                                    className="h-10 w-10 object-cover rounded"
                                  />
                                ) : (
                                  <div className="h-10 w-10 bg-muted rounded flex items-center justify-center">
                                    <Package className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </td>
                              <td className="p-2 text-sm font-mono">{product.gtin}</td>
                              <td className="p-2 text-sm">{product.name}</td>
                              <td className="p-2">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenEditDialog(product)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenDeleteDialog(product)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: Kompakte Karten-Liste */}
                    <div className="md:hidden">
                      {/* Mobile Header mit Alle auswählen */}
                      <div className="flex items-center gap-3 px-3 py-2 border-b bg-muted/30">
                        <Checkbox
                          checked={selectedProductIds.length === products.length && products.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                        <span className="text-xs text-muted-foreground font-medium">Alle auswählen</span>
                      </div>
                      {products.map((product) => (
                        <div
                          key={product.id}
                          className={`flex items-center gap-3 px-3 py-3 border-b hover:bg-muted/30 transition-colors ${
                            selectedProductIds.includes(product.id) ? 'bg-primary/5' : ''
                          }`}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onCheckedChange={() => handleSelectProduct(product.id)}
                            className="shrink-0"
                          />

                          {/* Bild */}
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="h-10 w-10 object-cover rounded shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 bg-muted rounded flex items-center justify-center shrink-0">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}

                          {/* Name + GTIN */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{product.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{product.gtin}</p>
                          </div>

                          {/* Aktionen */}
                          <div className="flex gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleOpenEditDialog(product)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => handleOpenDeleteDialog(product)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* P0: Pagination Controls */}
                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Pro Seite:</span>
                        <Select value={pageSize.toString()} onValueChange={(val) => handlePageSizeChange(Number(val))}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                        <span>
                          {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalProducts)} von {totalProducts}
                        </span>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(page - 1)}
                          disabled={page === 1}
                        >
                          <ChevronRight className="h-4 w-4 rotate-180" />
                          Zurück
                        </Button>
                        
                        {renderPaginationNumbers().map((pageNum, idx) => (
                          pageNum === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-2">...</span>
                          ) : (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              className="w-10"
                            >
                              {pageNum}
                            </Button>
                          )
                        ))}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(page + 1)}
                          disabled={page === totalPages}
                        >
                          Weiter
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Import-Historie</CardTitle>
                <CardDescription>Alle CSV-Imports</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                  </div>
                ) : importHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Noch keine Imports durchgeführt</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {importHistory.map((importRecord) => (
                      <Card 
                        key={importRecord.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/superadmin/products/report/${importRecord.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{importRecord.filename}</span>
                                {getStatusBadge(importRecord.status)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {new Date(importRecord.started_at).toLocaleString('de-DE')}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="text-center">
                                <div className="font-bold text-green-600">{importRecord.rows_success}</div>
                                <div className="text-xs text-muted-foreground">Erfolg</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-yellow-600">{importRecord.rows_skipped}</div>
                                <div className="text-xs text-muted-foreground">Übersprungen</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-red-600">{importRecord.rows_failed}</div>
                                <div className="text-xs text-muted-foreground">Fehler</div>
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vorschläge-Tab: Kunden-Produkte freigeben */}
          <TabsContent value="pending" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-500" />
                  Produkt-Vorschläge von Kunden
                </CardTitle>
                <CardDescription>
                  Diese Produkte wurden von Kunden neu angelegt und warten auf deine Freigabe für die globale Master-Datenbank.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingLoading ? (
                  <div className="text-center py-10 text-muted-foreground">Lade Vorschläge...</div>
                ) : pendingProducts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p className="font-medium">Keine offenen Vorschläge</p>
                    <p className="text-sm mt-1">Alle Produkte wurden bereits bearbeitet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingProducts.map((p) => (
                      <div key={p.id || p.gtin} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                        {(p.image_url || p.local_image_data) ? (
                          <img
                            src={p.image_url || p.local_image_data}
                            alt={p.product_name}
                            className="w-14 h-14 object-cover rounded-md border flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-14 h-14 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                            <Package className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{p.product_name}</p>
                          <p className="text-sm text-muted-foreground">EAN: {p.gtin}</p>
                          {p.created_by_name && (
                            <p className="text-xs text-muted-foreground mt-0.5">Von: {p.created_by_name}</p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => approvePending(p.id, p.product_name)}
                            disabled={processingId === p.id}
                            data-testid={`approve-product-${p.gtin}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Annehmen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => rejectPending(p.id, p.product_name)}
                            disabled={processingId === p.id}
                            data-testid={`reject-product-${p.gtin}`}
                          >
                            <X className="h-4 w-4 mr-1" /> Ablehnen
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Duplikate-Tab */}
          <TabsContent value="duplicates" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GitMerge className="h-5 w-5 text-red-500" />
                      Duplikate-Verwaltung
                    </CardTitle>
                    <CardDescription>
                      Produkte mit gleicher EAN oder ähnlicher EAN (gleiche letzten 7 Stellen – erkennt regionale Varianten). Wähle das Hauptprodukt, vergib einen finalen Namen und führe die Duplikate zusammen.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={fetchDuplicates}
                    disabled={duplicatesLoading}
                    data-testid="refresh-duplicates"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${duplicatesLoading ? 'animate-spin' : ''}`} />
                    Neu scannen
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {duplicatesLoading ? (
                  <div className="flex flex-col items-center justify-center h-48 gap-3">
                    <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    <p className="text-muted-foreground text-sm">Scanne Datenbank auf Duplikate...</p>
                  </div>
                ) : duplicates.length === 0 ? (
                  <div className="text-center py-16">
                    <CheckCircle className="h-16 w-16 mx-auto text-green-500 opacity-70 mb-4" />
                    <p className="font-semibold text-lg">Keine Duplikate gefunden</p>
                    <p className="text-muted-foreground text-sm mt-1">Die Master-Datenbank ist sauber.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
                      <ShieldAlert className="h-4 w-4 inline mr-2" />
                      <strong>{duplicates.length} Duplikat-{duplicates.length === 1 ? 'Gruppe' : 'Gruppen'} gefunden.</strong> Alle Lagerbestand- und MHD-Einträge werden automatisch auf das Hauptprodukt umgeleitet.
                    </div>

                    {duplicates.map((group, groupIndex) => (
                      <Card key={groupIndex} className="border-red-200 dark:border-red-900">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <Badge variant={group.reason === 'ean_exact' ? 'destructive' : 'outline'} className={group.reason === 'ean_suffix' ? 'border-orange-400 text-orange-600 bg-orange-50' : ''}>
                                {group.reason === 'ean_exact' ? 'Gleiche EAN' : 'Ähnliche EAN'}
                              </Badge>
                              <p className="text-sm text-muted-foreground">{group.reason_label}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">{group.products.length} Produkte</span>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {group.products.map((product) => {
                            const isSelected = selectedKeepIds[groupIndex] === product.id;
                            return (
                              <div
                                key={product.id}
                                data-testid={`dup-product-${product.id}`}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                                    : 'border-muted hover:border-muted-foreground/30 hover:bg-muted/30'
                                }`}
                                onClick={() => handleSelectKeep(groupIndex, product.id, product.product_name || product.name)}
                              >
                                {/* Radio indicator */}
                                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-green-500 bg-green-500' : 'border-muted-foreground'
                                }`}>
                                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>

                                {/* Product image */}
                                {product.image_url ? (
                                  <img
                                    src={product.image_url}
                                    alt={product.product_name}
                                    className="w-12 h-12 object-cover rounded border flex-shrink-0"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                                    <Package className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}

                                {/* Product info */}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{product.product_name || product.name || '(Kein Name)'}</p>
                                  <p className="text-xs text-muted-foreground font-mono">EAN: {product.ean}</p>
                                  {product.brand && (
                                    <p className="text-xs text-muted-foreground">Marke: {product.brand}</p>
                                  )}
                                </div>

                                {/* Crown icon for selected */}
                                {isSelected && (
                                  <div className="flex-shrink-0 flex items-center gap-1 text-green-600 text-xs font-semibold">
                                    <Crown className="h-4 w-4" />
                                    Hauptprodukt
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Beide beibehalten – immer sichtbar (kein Hauptprodukt nötig) */}
                          <div className="flex items-center justify-between pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                              Handelt es sich um verschiedene Produkte?
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                const group = duplicates[groupIndex];
                                const productIds = (group.products || []).map(p => p.id).filter(Boolean);
                                try {
                                  await axios.post(`${API}/superadmin/products/dismiss-duplicate-group`, {
                                    product_ids: productIds
                                  });
                                } catch (e) {
                                  console.warn('dismiss-duplicate-group fehlgeschlagen (non-critical):', e);
                                }
                                setDuplicates(prev => prev.filter((_, i) => i !== groupIndex));
                                setSelectedKeepIds(prev => { const n = {...prev}; delete n[groupIndex]; return n; });
                                setFinalNames(prev => { const n = {...prev}; delete n[groupIndex]; return n; });
                                toast.success('Gruppe als "kein Duplikat" markiert – wird beim nächsten Scan nicht mehr angezeigt');
                              }}
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`keep-both-${groupIndex}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Beide beibehalten
                            </Button>
                          </div>

                          {/* Finaler Produktname + Merge-Button (nur sichtbar wenn Hauptprodukt gewählt) */}
                          {selectedKeepIds[groupIndex] && (
                            <div className="mt-3 pt-3 border-t space-y-3">
                              <div className="space-y-1">
                                <Label htmlFor={`final-name-${groupIndex}`} className="text-sm font-medium">
                                  Finaler Produktname
                                </Label>
                                <Input
                                  id={`final-name-${groupIndex}`}
                                  data-testid={`final-name-input-${groupIndex}`}
                                  value={finalNames[groupIndex] || ''}
                                  onChange={(e) => setFinalNames(prev => ({ ...prev, [groupIndex]: e.target.value }))}
                                  placeholder="Namen des zusammengeführten Produkts eingeben..."
                                  className="text-sm"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Dieser Name wird für das Hauptprodukt und alle umgeleiteten Einträge gesetzt.
                                </p>
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {group.products.length - 1} Duplikat(e) werden gelöscht • Alle Verknüpfungen werden übertragen
                                </p>
                                <Button
                                  size="sm"
                                  disabled={!finalNames[groupIndex]?.trim() || mergeLoading}
                                  onClick={() => mergeDuplicates(groupIndex)}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  data-testid={`merge-group-${groupIndex}`}
                                >
                                  {mergeLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <GitMerge className="h-4 w-4 mr-2" />
                                  )}
                                  Zusammenführen
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Merge-Hinweis wenn kein Hauptprodukt gewählt */}
                          {!selectedKeepIds[groupIndex] && (
                            <p className="text-xs text-muted-foreground text-center py-1">
                              Klicke auf ein Produkt oben, um es als Hauptprodukt auszuwählen und zusammenzuführen
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* P0: Add Product Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Produkt hinzufügen</DialogTitle>
              <DialogDescription>
                Neues Produkt zur Master-Datenbank hinzufügen
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-gtin">GTIN / EAN *</Label>
                <Input
                  id="add-gtin"
                  placeholder="z.B. 4008400123456"
                  value={gtin}
                  onChange={(e) => setGtin(e.target.value)}
                  disabled={formLoading}
                />
                <p className="text-xs text-muted-foreground">
                  8, 12 oder 13 Ziffern
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-name">Produktname *</Label>
                <Input
                  id="add-name"
                  placeholder="z.B. Coca Cola 0.5L"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  disabled={formLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-image">Produktbild (optional)</Label>
                <div className="flex items-center gap-4">
                  {imagePreview && (
                    <img 
                      src={imagePreview} 
                      alt="Vorschau"
                      className="h-20 w-20 object-cover rounded border"
                    />
                  )}
                  <div className="flex-1">
                    <Input
                      id="add-image"
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      disabled={formLoading}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Max. 5 MB
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  resetForm();
                }}
                disabled={formLoading}
              >
                Abbrechen
              </Button>
              <Button onClick={handleCreateProduct} disabled={formLoading}>
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Erstelle...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Erstellen
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* P0: Edit Product Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Produkt bearbeiten</DialogTitle>
              <DialogDescription>
                Produktinformationen aktualisieren
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-gtin">GTIN / EAN *</Label>
                <Input
                  id="edit-gtin"
                  placeholder="z.B. 4008400123456"
                  value={gtin}
                  onChange={(e) => setGtin(e.target.value)}
                  disabled={formLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Produktname *</Label>
                <Input
                  id="edit-name"
                  placeholder="z.B. Coca Cola 0.5L"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  disabled={formLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-image">Produktbild (optional)</Label>
                <div className="flex items-center gap-4">
                  {imagePreview && (
                    <img 
                      src={imagePreview} 
                      alt="Vorschau"
                      className="h-20 w-20 object-cover rounded border"
                    />
                  )}
                  <div className="flex-1">
                    <Input
                      id="edit-image"
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      disabled={formLoading}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Max. 5 MB • Leer lassen für keine Änderung
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  resetForm();
                }}
                disabled={formLoading}
              >
                Abbrechen
              </Button>
              <Button onClick={handleUpdateProduct} disabled={formLoading}>
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Speichere...
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Speichern
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* P0: Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Produkt wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                <div className="space-y-2">
                  <p>Sie sind dabei, folgendes Produkt zu löschen:</p>
                  <div className="bg-muted p-3 rounded-md">
                    <p className="font-medium">{selectedProduct?.name}</p>
                    <p className="text-sm text-muted-foreground">GTIN: {selectedProduct?.gtin}</p>
                  </div>
                  <p className="text-yellow-600 dark:text-yellow-500 flex items-start gap-2 mt-4">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Hinweis:</strong> Das Produkt wird aus der Master-Datenbank entfernt. 
                      Bestehende MHD-Einträge bleiben unberührt.
                    </span>
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={formLoading}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteProduct}
                disabled={formLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Lösche...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* P0: Bulk Delete Confirmation Dialog */}
        <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {selectedProductIds.length} {selectedProductIds.length === 1 ? 'Produkt' : 'Produkte'} wirklich löschen?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <div className="space-y-2">
                  <p>
                    Sie sind dabei, <strong>{selectedProductIds.length}</strong> {selectedProductIds.length === 1 ? 'Produkt' : 'Produkte'} zu löschen.
                  </p>
                  <p className="text-yellow-600 dark:text-yellow-500 flex items-start gap-2 mt-4">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      <strong>Hinweis:</strong> Die Produkte werden aus der Master-Datenbank entfernt. 
                      Bestehende MHD-Einträge bleiben unberührt.
                    </span>
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={formLoading}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkDelete}
                disabled={formLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Lösche...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {selectedProductIds.length} {selectedProductIds.length === 1 ? 'Produkt' : 'Produkte'} löschen
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Duplicate Confirmation Dialog */}
        <Dialog open={dupConfirmOpen} onOpenChange={(open) => { if (!open) { setDupConfirmOpen(false); setDupConfirmData(null); } }}>
          <DialogContent className="sm:max-w-[620px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                Mögliches Duplikat gefunden
              </DialogTitle>
              <DialogDescription>
                Im Master-Katalog wurden ähnliche Produkte gefunden. Bitte prüfe, ob es sich um ein Duplikat handelt.
              </DialogDescription>
            </DialogHeader>

            {dupConfirmData && (
              <div className="space-y-4 py-2">
                {/* New product */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Neues Produkt (zur Freigabe)</p>
                  <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-green-200 bg-green-50 dark:bg-green-900/20">
                    {dupConfirmData.pendingProduct?.image ? (
                      <img src={dupConfirmData.pendingProduct.image} alt="" className="w-12 h-12 object-cover rounded border flex-shrink-0" onError={(e) => e.target.style.display='none'} />
                    ) : (
                      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                        <Package className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-sm">{dupConfirmData.pendingProduct?.product_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">EAN: {dupConfirmData.pendingProduct?.gtin}</p>
                    </div>
                  </div>
                </div>

                {/* Possible duplicates */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Bereits im Master-Katalog ({dupConfirmData.possibleDuplicates.length})
                  </p>
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {dupConfirmData.possibleDuplicates.map((dup, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                        {dup.image_url ? (
                          <img src={dup.image_url} alt="" className="w-12 h-12 object-cover rounded border flex-shrink-0" onError={(e) => e.target.style.display='none'} />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-sm">{dup.product_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">EAN: {dup.ean}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                  Handelt es sich um dasselbe Produkt, lehne den Vorschlag ab. Ist es ein anderes Produkt, klicke auf "Trotzdem freigeben".
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setDupConfirmOpen(false); setDupConfirmData(null); }}
                data-testid="dup-confirm-cancel"
              >
                Abbrechen
              </Button>
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (dupConfirmData) rejectPending(dupConfirmData.mappingId, dupConfirmData.productName);
                  setDupConfirmOpen(false);
                  setDupConfirmData(null);
                }}
                data-testid="dup-confirm-reject"
              >
                <X className="h-4 w-4 mr-1" /> Als Duplikat ablehnen
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={forceApprovePending}
                data-testid="dup-confirm-force-approve"
              >
                <Check className="h-4 w-4 mr-1" /> Trotzdem freigeben
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
